import { normalizeRequestFormStepId } from './formStepIds';
import {
  BASE_REQUEST_FORM_STEP_IDS,
  getDefaultEnabledStepIdsForNewRequest,
  getLegacyFullEnabledStepIds,
  getRequestFormStepCatalogEntry,
  isScopePickerComingSoon,
  isOptionalRequestFormStepId,
  OPTIONAL_REQUEST_FORM_STEP_IDS,
  REQUEST_FORM_STEP_REGISTRY,
  sortStepIdsByRegistry,
  type RequestFormStepCatalogEntry,
  type RequestFormStepId,
} from '../config/requestFormStepRegistry';

const KNOWN_STEP_IDS = new Set(
  REQUEST_FORM_STEP_REGISTRY.map(entry => entry.id),
);

function normalizeEnabledStepIds(raw: readonly string[]): RequestFormStepId[] {
  const seen = new Set<string>();
  const out: RequestFormStepId[] = [];

  for (const id of raw) {
    const normalized = normalizeRequestFormStepId(id);
    if (!KNOWN_STEP_IDS.has(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    out.push(normalized as RequestFormStepId);
  }

  for (const baseId of BASE_REQUEST_FORM_STEP_IDS) {
    if (!seen.has(baseId)) {
      out.push(baseId);
      seen.add(baseId);
    }
  }

  return sortStepIdsByRegistry(out) as RequestFormStepId[];
}

/**
 * Resolve stored `enabledStepIds` to the effective enabled set.
 * `null` / missing → legacy full optional set (existing production requests).
 */
export function resolveEnabledStepIds(
  stored: string[] | null | undefined,
): RequestFormStepId[] {
  if (stored == null) {
    return getLegacyFullEnabledStepIds();
  }
  if (stored.length === 0) {
    return getDefaultEnabledStepIdsForNewRequest();
  }
  return normalizeEnabledStepIds(stored);
}

export function isLegacyEnabledStepsMode(
  stored: string[] | null | undefined,
): boolean {
  return stored == null;
}

/** True when the resolved enabled set matches pre-#1330 legacy optional scopes. */
export function isLegacyResolvedEnabledStepSet(
  enabledStepIds: readonly string[],
): boolean {
  const legacy = getLegacyFullEnabledStepIds();
  if (legacy.length !== enabledStepIds.length) {
    return false;
  }
  const enabled = new Set(enabledStepIds);
  return legacy.every(id => enabled.has(id));
}

export function resolveRequestFormStepCatalog(
  stored: string[] | null | undefined,
): RequestFormStepCatalogEntry[] {
  const ids = resolveEnabledStepIds(stored);
  return ids
    .map(id => getRequestFormStepCatalogEntry(id))
    .filter((entry): entry is RequestFormStepCatalogEntry => entry != null);
}

export function getAddableOptionalStepIds(
  stored: string[] | null | undefined,
): RequestFormStepId[] {
  const enabled = new Set(resolveEnabledStepIds(stored));
  return OPTIONAL_REQUEST_FORM_STEP_IDS.filter(id => {
    if (enabled.has(id)) {
      return false;
    }
    return !isScopePickerComingSoon(id);
  });
}

export function getRemovableOptionalStepIds(
  stored: string[] | null | undefined,
): RequestFormStepId[] {
  const enabled = resolveEnabledStepIds(stored);
  return enabled.filter(isOptionalRequestFormStepId);
}

export function mergeEnabledStepIdsAfterAdd(
  stored: string[] | null | undefined,
  stepId: string,
): RequestFormStepId[] {
  const normalized = normalizeRequestFormStepId(stepId);
  if (!isOptionalRequestFormStepId(normalized)) {
    return resolveEnabledStepIds(stored);
  }
  if (isScopePickerComingSoon(normalized)) {
    return resolveEnabledStepIds(stored);
  }
  const current = resolveEnabledStepIds(stored);
  if (current.includes(normalized)) {
    return current;
  }
  return normalizeEnabledStepIds([...current, normalized]);
}

export function mergeEnabledStepIdsAfterRemove(
  stored: string[] | null | undefined,
  stepId: string,
): RequestFormStepId[] {
  const normalized = normalizeRequestFormStepId(stepId);
  if (!isOptionalRequestFormStepId(normalized)) {
    return resolveEnabledStepIds(stored);
  }
  const current = resolveEnabledStepIds(stored);
  return normalizeEnabledStepIds(current.filter(id => id !== normalized));
}

export function isStepEnabled(
  stored: string[] | null | undefined,
  stepId: string,
): boolean {
  const normalized = normalizeRequestFormStepId(stepId);
  return resolveEnabledStepIds(stored).includes(
    normalized as RequestFormStepId,
  );
}
